import React from "react";
import {
  FileText, Plus, Search, Check, X, User, Car, Wrench, Download, Edit, Trash2,
  Shield, Zap, Flame, Settings, CheckCircle, Clock,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { StatCard, SectionCard, EmptyState, SearchInput, ActionButton, StatusBadge } from "./AdminUI";

const INTERVENTION_BADGES = {
  gain_puissance: { icon: Zap, label: "Puissance", color: "bg-blue-500/15 text-blue-400 border-blue-500/25" },
  conversion_ethanol: { icon: Flame, label: "Éthanol", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" },
  autres: { icon: Settings, label: "Autre", color: "bg-amber-500/15 text-amber-400 border-amber-500/25" },
};

const DischargesTab = ({ ctx }) => {
  const {
    discharges, dischargeSearch, setDischargeSearch, fetchDischarges,
    showDischargeForm, setShowDischargeForm, editingDischargeId, setEditingDischargeId,
    dischargeForm, setDischargeForm, handleCreateDischarge,
    generateDischargePDF, signDischarge, editDischarge, deleteDischarge,
  } = ctx;

  const counts = {
    total: discharges.length,
    signed: discharges.filter((d) => d.signed).length,
    pending: discharges.filter((d) => !d.signed).length,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield size={20} className="text-[#FF3B30]" />
            Décharges de responsabilité
          </h2>
          <p className="text-white/50 text-xs mt-0.5">
            Gérez les décharges pour les interventions de gain de puissance et conversion éthanol
          </p>
        </div>
        <button
          onClick={() => {
            setShowDischargeForm(true);
            setEditingDischargeId(null);
            setDischargeForm({
              client_name: "", client_address: "", client_phone: "",
              vehicle_brand: "", vehicle_model: "", vehicle_plate: "",
              vehicle_vin: "", vehicle_mileage: "", intervention_type: [],
              other_intervention: "", signed: false, signature_date: "",
            });
          }}
          className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus size={15} /> Nouvelle décharge
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={FileText} label="Total décharges" value={counts.total} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={CheckCircle} label="Signées" value={counts.signed} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={Clock} label="En attente" value={counts.pending} color="text-amber-400" accent="bg-amber-500/10" />
      </div>

      {/* Search */}
      <SectionCard title={null}>
        <SearchInput
          value={dischargeSearch}
          onChange={(e) => { setDischargeSearch(e.target.value); fetchDischarges(e.target.value); }}
          placeholder="Rechercher par nom, plaque..."
          icon={Search}
        />
      </SectionCard>

      {/* Table */}
      <SectionCard title={`Liste (${discharges.length})`} icon={FileText}>
        {discharges.length === 0 ? (
          <EmptyState icon={FileText} title="Aucune décharge trouvée" description="Créez votre première décharge avec le bouton ci-dessus." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left">
              <thead className="border-b border-white/[0.06]">
                <tr className="text-white/40 text-[11px] uppercase tracking-wider">
                  <th className="py-3 pr-4">Client</th>
                  <th className="py-3 pr-4">Véhicule</th>
                  <th className="py-3 pr-4">Intervention</th>
                  <th className="py-3 pr-4">Statut</th>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-0 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {discharges.map((d) => (
                  <tr key={d.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 pr-4">
                      <p className="text-white font-medium text-sm">{d.client_name}</p>
                      <p className="text-white/50 text-xs mt-0.5">{d.client_phone}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-[#FF3B30] font-mono font-bold text-sm">{d.vehicle_plate}</p>
                      <p className="text-white/50 text-xs mt-0.5">{d.vehicle_brand} {d.vehicle_model}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {d.intervention_type?.map((type) => {
                          const cfg = INTERVENTION_BADGES[type];
                          if (!cfg) return null;
                          const Icon = cfg.icon;
                          return (
                            <span key={type} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${cfg.color}`}>
                              <Icon size={10} /> {cfg.label}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      {d.signed ? (
                        <StatusBadge config={{ color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", dot: "bg-emerald-400", label: "Signé" }} />
                      ) : (
                        <StatusBadge config={{ color: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400", label: "En attente" }} />
                      )}
                    </td>
                    <td className="py-3 pr-4 text-white/50 text-xs">
                      {new Date(d.created_at).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex gap-1.5 justify-end">
                        <ActionButton icon={Download} label="PDF" color="green" testid={`discharge-pdf-btn-${d.id}`} onClick={() => generateDischargePDF(d)} />
                        {!d.signed && (
                          <ActionButton icon={Check} label="Signer" color="blue" onClick={() => signDischarge(d.id)} />
                        )}
                        <ActionButton icon={Edit} label="" color="white" onClick={() => editDischarge(d)} title="Modifier" />
                        <ActionButton icon={Trash2} label="" color="red" onClick={() => deleteDischarge(d.id)} title="Supprimer" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Create/Edit Modal */}
      {showDischargeForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#141414]/95 backdrop-blur">
              <h3 className="text-lg font-bold text-white">
                {editingDischargeId ? "Modifier la décharge" : "Nouvelle décharge de responsabilité"}
              </h3>
              <button onClick={() => setShowDischargeForm(false)} className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Client */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <User size={14} className="text-[#FF3B30]" /> Informations client
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Nom et prénom *</label>
                    <Input value={dischargeForm.client_name} onChange={(e) => setDischargeForm({ ...dischargeForm, client_name: e.target.value })} placeholder="Jean Dupont" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Téléphone</label>
                    <Input value={dischargeForm.client_phone} onChange={(e) => setDischargeForm({ ...dischargeForm, client_phone: e.target.value })} placeholder="06 12 34 56 78" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Adresse</label>
                    <Input value={dischargeForm.client_address} onChange={(e) => setDischargeForm({ ...dischargeForm, client_address: e.target.value })} placeholder="123 rue de la Paix, 63000 Clermont-Ferrand" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                </div>
              </div>

              {/* Vehicle */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <Car size={14} className="text-[#FF3B30]" /> Informations véhicule
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Marque</label>
                    <Input value={dischargeForm.vehicle_brand} onChange={(e) => setDischargeForm({ ...dischargeForm, vehicle_brand: e.target.value })} placeholder="Peugeot" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Modèle</label>
                    <Input value={dischargeForm.vehicle_model} onChange={(e) => setDischargeForm({ ...dischargeForm, vehicle_model: e.target.value })} placeholder="308 GTi" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Immatriculation *</label>
                    <Input value={dischargeForm.vehicle_plate} onChange={(e) => setDischargeForm({ ...dischargeForm, vehicle_plate: e.target.value.toUpperCase() })} placeholder="AA-123-BB" className="bg-[#141414] border-white/[0.06] text-[#FF3B30] font-bold h-9 text-sm" />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Kilométrage</label>
                    <Input type="number" value={dischargeForm.vehicle_mileage} onChange={(e) => setDischargeForm({ ...dischargeForm, vehicle_mileage: e.target.value })} placeholder="85000" className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Numéro de série (VIN)</label>
                    <Input value={dischargeForm.vehicle_vin} onChange={(e) => setDischargeForm({ ...dischargeForm, vehicle_vin: e.target.value.toUpperCase() })} placeholder="VF3XXXXXXXXXXXXXX" className="bg-[#141414] border-white/[0.06] text-white font-mono h-9 text-sm" />
                  </div>
                </div>
              </div>

              {/* Intervention */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <Wrench size={14} className="text-[#FF3B30]" /> Nature de l'intervention
                </h4>
                <div className="space-y-2">
                  {[
                    { key: "gain_puissance", label: "Gain de puissance", icon: Zap },
                    { key: "conversion_ethanol", label: "Conversion éthanol / E85", icon: Flame },
                    { key: "autres", label: "Autres", icon: Settings },
                  ].map((opt) => {
                    const checked = dischargeForm.intervention_type.includes(opt.key);
                    const Icon = opt.icon;
                    return (
                      <label key={opt.key} className={`flex items-center gap-3 cursor-pointer p-2.5 rounded-md border transition-colors ${
                        checked ? "bg-[#FF3B30]/10 border-[#FF3B30]/30" : "bg-[#141414] border-white/[0.06] hover:border-white/[0.12]"
                      }`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const types = e.target.checked
                              ? [...dischargeForm.intervention_type, opt.key]
                              : dischargeForm.intervention_type.filter((t) => t !== opt.key);
                            setDischargeForm({ ...dischargeForm, intervention_type: types });
                          }}
                          className="w-4 h-4 accent-[#FF3B30]"
                        />
                        <Icon size={15} className={checked ? "text-[#FF3B30]" : "text-white/40"} />
                        <span className="text-white text-sm">{opt.label}</span>
                      </label>
                    );
                  })}
                  {dischargeForm.intervention_type.includes("autres") && (
                    <Input
                      value={dischargeForm.other_intervention}
                      onChange={(e) => setDischargeForm({ ...dischargeForm, other_intervention: e.target.value })}
                      placeholder="Précisez l'intervention..."
                      className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm mt-2"
                    />
                  )}
                </div>
              </div>

              {/* Signature */}
              <div className="bg-[#1C1C1E] border border-white/[0.04] rounded-lg p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                  <FileText size={14} className="text-[#FF3B30]" /> Statut de signature
                </h4>
                <label className={`flex items-center gap-3 cursor-pointer p-2.5 rounded-md border transition-colors ${
                  dischargeForm.signed ? "bg-emerald-500/10 border-emerald-500/30" : "bg-[#141414] border-white/[0.06] hover:border-white/[0.12]"
                }`}>
                  <input
                    type="checkbox"
                    checked={dischargeForm.signed}
                    onChange={(e) => setDischargeForm({
                      ...dischargeForm,
                      signed: e.target.checked,
                      signature_date: e.target.checked ? new Date().toLocaleDateString("fr-FR") : "",
                    })}
                    className="w-4 h-4 accent-emerald-500"
                  />
                  <CheckCircle size={15} className={dischargeForm.signed ? "text-emerald-400" : "text-white/40"} />
                  <span className="text-white text-sm">Document signé par le client</span>
                </label>
                {dischargeForm.signed && (
                  <div className="mt-3 max-w-xs">
                    <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Date de signature</label>
                    <Input
                      value={dischargeForm.signature_date}
                      onChange={(e) => setDischargeForm({ ...dischargeForm, signature_date: e.target.value })}
                      placeholder="11/02/2026"
                      className="bg-[#141414] border-white/[0.06] text-white h-9 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#141414]/95 backdrop-blur">
              <button
                onClick={() => setShowDischargeForm(false)}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2.5 rounded-md text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateDischarge}
                className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] text-white py-2.5 rounded-md text-sm font-bold transition-colors"
              >
                {editingDischargeId ? "Mettre à jour" : "Créer la décharge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DischargesTab;
